import { useMemo } from 'react';
import * as React from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

import { RadioGroup, Title } from '@restworld/ui-ds';
import { useNotificationFrequencyOptions } from '@restworld/utils-common';

import { notificationModalStateAtom, SaveResearchFrequency, notificationChangeAtom } from './save-research-tab.atoms';
import SavedResearchUnSubscribeReason from './saved-research-unsubscribe-reason';
import { useSavedResearchProps } from './save-research-tab.hooks';

const circleIcon = <CheckCircleIcon className="text-green-700 w-[42px] h-[42px]" />;
export const SavedResearchNotificationModalContent = () => {
  const notificationFrequencyOptions = useNotificationFrequencyOptions();
  const { t: commonT } = useTranslation('common');
  const notificationOptions = useMemo(
    () => [
      ...notificationFrequencyOptions,
      ...[
        {
          id: 'unsubscribe',
          key: 'unsubscribe',
          label: commonT('stop_receiving_ads'),
        },
      ],
    ],
    [notificationFrequencyOptions, commonT]
  );

  const [notificationModalState] = useAtom(notificationModalStateAtom);
  const [notificationChange, setNotificationChange] = useAtom(notificationChangeAtom);
  const { title: headerTitle } = useSavedResearchProps();
  const { t } = useTranslation('user-settings');
  const contentEl = useMemo(() => {
    switch (notificationModalState) {
      case 'unsubscribe_review_success':
        return (
          <div tw="flex flex-col gap-8 items-center justify-center">
            {circleIcon}
            <div tw="flex flex-col gap-2 items-center justify-center">
              <Title
                content={t('unsubscribed_notifications_for_this_search_unsubscribed')}
                isCenter
                twStyle={tw`leading-[28.8px]`}
                fontSize="superLarge"
                fontWeight="semibold"
              />
              <Title
                content={t('update_preferences_when_signing_up_later')}
                isCenter
                twStyle={tw`leading-[24px]`}
                fontSize="base"
                fontWeight="normal"
                textColor="gray-500"
              />
            </div>
          </div>
        );
      case 'unsubscribe_user_review':
        return <SavedResearchUnSubscribeReason />;
      case 'frequency_change_success':
        return (
          <div tw="flex flex-col gap-8 items-center justify-center">
            {circleIcon}
            <Title
              content={t('preferences_have_been_saved')}
              isCenter
              twStyle={tw`leading-[28.8px] mb-7`}
              fontSize="superLarge"
              fontWeight="semibold"
            />
          </div>
        );
      case 'delete_save_research_success':
        return (
          <div tw="flex flex-col gap-8 items-center justify-center mb-7">
            {circleIcon}
            <Title
              content={`${t('deleted_the_search', { title: `“${headerTitle}”` })}`}
              twStyle={tw`leading-[28.8px]`}
              fontSize="superLarge"
              fontWeight="semibold"
              isCenter
            />
          </div>
        );
      default:
        return (
          <div tw="flex flex-col gap-8">
            <Title
              content={t('select_notification_frequency')}
              textColor="gray-600"
              fontWeight="normal"
              fontSize="base"
              twStyle={tw`leading-6`}
            />
            <RadioGroup
              value={notificationChange}
              id="notification_frequency"
              options={notificationOptions}
              onChange={(value) => {
                setNotificationChange(value as SaveResearchFrequency);
              }}
              returnKey="key"
              testId="notification_frequency"
            />
          </div>
        );
    }
  }, [headerTitle, notificationChange, notificationModalState, notificationOptions, setNotificationChange, t]);
  return contentEl;
};
export default SavedResearchNotificationModalContent;
